<div class="p-8 font-sans text-gray-900">
  <div class="text-gray-500 text-sm mb-4">
    <span routerLink="/admin/state-associations" class="cursor-pointer text-blue-600 hover:underline">State Associations</span>
    <span>› {{ selectedStateAssociation?.state }}</span>
  </div>

  <div class="flex items-center justify-between">
    <div class="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded-lg font-semibold overflow-hidden">
      <img
        *ngIf="logoImageUrl"
        [src]="logoImageUrl"
        [alt]="selectedStateAssociation?.name + ' logo'"
        class="w-full h-full object-contain"
      />
      <span *ngIf="!logoImageUrl">LOGO</span>
    </div>
    <div class="flex-1 ml-4">
      <h1 class="text-gray-800">{{ selectedStateAssociation?.state }}</h1>
      <p class="m-0 text-base text-gray-700 leading-relaxed" *ngIf="selectedStateAssociation?.name">
        {{ selectedStateAssociation?.name}}
      </p>
      <a
        *ngIf="selectedStateAssociation?.url"
        [href]="selectedStateAssociation?.url"
        target="_blank"
        rel="noopener noreferrer"
        class="block w-fit mb-2 text-sm text-blue-500 no-underline transition-colors hover:text-blue-600 hover:underline"
      >
        {{ selectedStateAssociation?.url }}
        <span class="text-xs">↗</span>
      </a>
      <span
        class="inline-block py-1.5 px-4 text-sm font-medium rounded-full text-white border-none w-fit"
        [ngClass]="{
          'bg-gray-400': selectedStateAssociation?.memberLevel === 0,
          'bg-gray-900': selectedStateAssociation?.memberLevel === 1,
          'bg-amber-500': selectedStateAssociation?.memberLevel === 2,
          'bg-purple-700': selectedStateAssociation?.memberLevel === 3
        }"
      >
        Level {{ selectedStateAssociation?.memberLevel }}
      </span>
    </div>

    <button
      class="bg-white text-gray-700 border border-gray-300 rounded-lg px-4 py-2 cursor-pointer font-medium text-sm hover:bg-gray-600 hover:border-gray-400 hover:text-white transition-colors"
      (click)="editDetails()"
    >
      📝️ Edit Details
    </button>
  </div>

  <div class="mt-8">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-semibold text-gray-900 m-0">State Association Members ({{ agencies?.length || 0}})</h2>
      <div class="flex gap-3">
        <button
          class="bg-white text-gray-700 border border-gray-300 rounded-lg px-4 py-2 cursor-pointer font-medium text-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
          (click)="openBulkUploadModal()"
        >
          📊 Bulk Upload
        </button>
        <button
          class="bg-gray-900 text-white border-none rounded-lg px-4 py-2 cursor-pointer font-medium hover:bg-slate-800"
          (click)="addAgency()"
        >
          ➕ Add Agency
        </button>
      </div>
    </div>

    <div class="w-[280px] mb-4 relative">
      <input
        type="text"
        placeholder="Search agencies..."
        [(ngModel)]="searchTerm"
        (input)="filterAgencies()"
        class="w-full py-2.5 pr-3 pl-9 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-blue-600 focus:ring-[3px] focus:ring-blue-100"
      />
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor">
        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
      </svg>
    </div>

    <!-- CDK Table -->
    <div class="overflow-hidden rounded-[10px] border border-gray-200 bg-white relative min-h-[200px]">
      <!-- Loading Overlay -->
      <div
        *ngIf="isLoadingAgencies"
        class="absolute inset-0 bg-white bg-opacity-90 flex flex-col items-center justify-center z-10"
      >
        <div class="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p class="text-sm text-gray-600">Loading agencies...</p>
      </div>

      <!-- Table (always rendered) -->
      <table
        cdk-table
        [dataSource]="filteredAgencies"
        class="w-full border-collapse"
        [class.opacity-50]="isLoadingAgencies"
      >
        <!-- Agency Name Column -->
        <ng-container cdkColumnDef="name">
          <th
            cdk-header-cell
            *cdkHeaderCellDef
            class="px-4 py-3 border-b border-gray-200 text-left bg-gray-50 font-semibold cursor-pointer hover:bg-gray-100 transition-colors select-none"
            (click)="sortByColumn('name')"
          >
            <div class="flex items-center gap-1">
              Agency Name
              <span class="text-blue-600 text-xs">{{ getSortIndicator('name') }}</span>
            </div>
          </th>
          <td
            cdk-cell
            *cdkCellDef="let agency"
            class="px-4 py-3 border-b border-gray-200"
          >
            {{ agency.name }}
          </td>
        </ng-container>

        <!-- Agency ID Column -->
        <ng-container cdkColumnDef="agencyId">
          <th
            cdk-header-cell
            *cdkHeaderCellDef
            class="px-4 py-3 border-b border-gray-200 text-left bg-gray-50 font-semibold cursor-pointer hover:bg-gray-100 transition-colors select-none"
            (click)="sortByColumn('agencyId')"
          >
            <div class="flex items-center gap-1">
              Agency ID
              <span class="text-blue-600 text-xs">{{ getSortIndicator('agencyId') }}</span>
            </div>
          </th>
          <td
            cdk-cell
            *cdkCellDef="let agency"
            class="px-4 py-3 border-b border-gray-200"
          >
            {{ agency.agencyId }}
          </td>
        </ng-container>

        <!-- Subscription Column -->
        <ng-container cdkColumnDef="subscription">
          <th
            cdk-header-cell
            *cdkHeaderCellDef
            class="px-4 py-3 border-b border-gray-200 text-left bg-gray-50 font-semibold cursor-pointer hover:bg-gray-100 transition-colors select-none"
            (click)="sortByColumn('subscription')"
          >
            <div class="flex items-center gap-1">
              Subscription
              <span class="text-blue-600 text-xs">{{ getSortIndicator('subscription') }}</span>
            </div>
          </th>
          <td
            cdk-cell
            *cdkCellDef="let agency"
            class="px-4 py-3 border-b border-gray-200"
          >
            {{ getSubscriptionPlan(agency) }}
          </td>
        </ng-container>

        <!-- Location Column -->
        <ng-container cdkColumnDef="location">
          <th
            cdk-header-cell
            *cdkHeaderCellDef
            class="px-4 py-3 border-b border-gray-200 text-left bg-gray-50 font-semibold cursor-pointer hover:bg-gray-100 transition-colors select-none"
            (click)="sortByColumn('location')"
          >
            <div class="flex items-center gap-1">
              Location
              <span class="text-blue-600 text-xs">{{ getSortIndicator('location') }}</span>
            </div>
          </th>
          <td
            cdk-cell
            *cdkCellDef="let agency"
            class="px-4 py-3 border-b border-gray-200"
          >
            {{ getLocation(agency) }}
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container cdkColumnDef="actions">
          <th
            cdk-header-cell
            *cdkHeaderCellDef
            class="px-4 py-3 border-b border-gray-200 text-left bg-gray-50 font-semibold"
          >
            Actions
          </th>
          <td
            cdk-cell
            *cdkCellDef="let agency"
            class="px-4 py-3 border-b border-gray-200"
          >
            <button
              class="border-none bg-transparent cursor-pointer text-lg hover:opacity-70 transition-opacity"
              (click)="deleteAgency(agency)"
            >
              🗑️
            </button>
          </td>
        </ng-container>

        <!-- Header Row -->
        <tr
          cdk-header-row
          *cdkHeaderRowDef="displayedColumns"
        ></tr>

        <!-- Data Rows -->
        <tr
          cdk-row
          *cdkRowDef="let agency; columns: displayedColumns"
        ></tr>
      </table>

      <!-- Empty State (only show when not loading AND no data) -->
      <div
        *ngIf="filteredAgencies.length === 0 && !isLoadingAgencies"
        class="px-6 py-12 text-center text-gray-500"
      >
        <p class="text-lg font-medium">No agencies found</p>
        <p class="text-sm mt-1">Try adjusting your search or add a new agency</p>
      </div>
    </div>
  </div>
</div>

<!-- Edit Modal -->
<div
  class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-[1000] p-5"
  *ngIf="isEditModalOpen"
  (click)="onBackdropClick($event)"
>
  <div class="bg-white rounded-xl w-[420px] max-w-[90%] max-h-[calc(100vh-40px)] shadow-2xl relative flex flex-col">
    <!-- Modal Header -->
    <div class="px-8 py-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
      <h3 class="text-lg font-semibold text-gray-900 m-0">Edit Association Details</h3>
      <button
        class="bg-transparent border-none text-2xl text-gray-400 cursor-pointer p-0 w-6 h-6 flex items-center justify-center leading-none hover:text-gray-500 transition-colors"
        (click)="cancelEdit()"
        aria-label="Close"
      >
        ×
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-8 overflow-y-auto overflow-x-hidden flex-1 min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2" for="associationName">State Association Name</label>
        <input
          type="text"
          id="associationName"
          class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-blue-600 focus:ring-[3px] focus:ring-blue-100"
          [(ngModel)]="editAssociationName"
          placeholder="Enter association name"
        />
        <div class="text-xs text-gray-400 mt-2">Used in association with the logo on profile and agency profile pages.</div>
      </div>
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2" for="memberLevel">State Association Level</label>
        <div class="relative">
          <select
            id="memberLevel"
            class="w-full px-3 py-2.5 pr-9 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white cursor-pointer appearance-none focus:outline-none focus:border-blue-600 focus:ring-[3px] focus:ring-blue-100"
            [(ngModel)]="editMemberLevel"
          >
            <option [ngValue]="0">Not a Member</option>
            <option [ngValue]="1">Level 1</option>
            <option [ngValue]="2">Level 2</option>
            <option [ngValue]="3">Level 3</option>
          </select>
          <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none text-gray-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="currentColor">
            <path d="M6 9L1 4h10z"/>
          </svg>
        </div>
      </div>
      <!-- State Association URL -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2" for="associationUrl">State Association URL</label>
        <input
          type="url"
          id="associationUrl"
          class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-blue-600 focus:ring-[3px] focus:ring-blue-100"
          [(ngModel)]="editAssociationUrl"
          placeholder="https://example.com"
        />
        <div class="text-xs text-gray-400 mt-2">URL must contain http:// or https://</div>
      </div>

      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2">Association Logo</label>

        <!-- UPDATED: Using drag-drop-zone component -->
        <app-drag-and-drop
          accept=".png,.jpg,.jpeg"
          (fileDropped)="handleLogoFile($event)"
        >
          <div class="w-12 h-12 mx-auto mb-3 bg-gray-200 rounded-md flex items-center justify-center text-gray-400 text-2xl">🖼️</div>
          <div class="text-sm text-gray-600 mb-1">Click to upload or drag and drop</div>
          <div class="text-xs text-gray-400">PNG, JPG up to 2MB</div>
        </app-drag-and-drop>

        <div *ngIf="editLogoUrl" class="mt-4 text-center">
          <img [src]="editLogoUrl" alt="Logo Preview" class="max-w-[200px] max-h-[100px] border border-gray-200 rounded inline-block" />
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="px-8 py-5 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white">
      <button
        class="py-2.5 px-5 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400"
        (click)="cancelEdit()"
      >
        Cancel
      </button>
      <button
        class="py-2.5 px-6 border-none rounded-lg bg-gray-900 text-white text-sm font-medium cursor-pointer transition-all hover:bg-gray-800"
        (click)="saveChanges()"
      >
        Save Changes
      </button>
    </div>
  </div>
</div>

<!-- Add Agency Modal -->
<div
  class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-[1000] p-5"
  *ngIf="isAddAgencyModalOpen"
  (click)="onAddAgencyBackdropClick($event)"
>
  <div class="bg-white rounded-xl w-[420px] max-w-[90%] max-h-[calc(100vh-40px)] shadow-2xl relative flex flex-col" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="px-8 py-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
      <h3 class="text-lg font-semibold text-gray-900 m-0">Add New Agency</h3>
      <button
        class="bg-transparent border-none text-2xl text-gray-400 cursor-pointer p-0 w-6 h-6 flex items-center justify-center leading-none hover:text-gray-500 transition-colors"
        (click)="cancelAddAgency()"
        aria-label="Close"
      >
        ×
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-8 overflow-y-auto overflow-x-hidden flex-1 min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 mb-2" for="agencyId">Agency ID</label>
        <div class="relative">
          <input
            type="text"
            id="agencyId"
            class="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-blue-600 focus:ring-[3px] focus:ring-blue-100"
            [(ngModel)]="searchAgencyId"
            (ngModelChange)="agencySearch$.next($event)"
          />
          <span class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">🔍</span>
        </div>
        <div *ngIf="agencyFound" class="flex items-center gap-2 mt-2 text-[13px] text-emerald-600">
          ✓ Agency found and details loaded
        </div>
        <div *ngIf="agencyNotFound" class="flex items-center gap-2 mt-2 text-[13px] text-red-600">
          ✗ Agency not found
        </div>
      </div>

      <div *ngIf="agencyFound" class="bg-gray-50 rounded-lg p-5 mt-2">
        <div class="text-[13px] font-semibold text-gray-500 uppercase tracking-wide mb-4">Agency Information</div>

        <div class="mb-4">
          <label class="block text-xs font-medium text-gray-500 mb-1">Agency Name</label>
          <div class="text-sm text-gray-900 font-medium">{{ selectedAgency?.name }}</div>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="min-w-0">
            <label class="block text-xs font-medium text-gray-500 mb-1">City</label>
            <div class="text-sm text-gray-900 font-medium">{{ selectedAgency?.address?.city }}</div>
          </div>
          <div class="min-w-0">
            <label class="block text-xs font-medium text-gray-500 mb-1">State</label>
            <div class="text-sm text-gray-900 font-medium">{{ selectedAgency?.address?.regionCode }}</div>
          </div>
        </div>

        <div class="mb-0">
          <label class="block text-xs font-medium text-gray-500 mb-1">Subscription Type</label>
          <div class="text-sm text-gray-900 font-medium">
            {{ selectedAgency?.subscriptions?.[0]?.plan?.name || 'No subscription plan' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="px-8 py-5 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white">
      <button
        class="py-2.5 px-5 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400"
        (click)="cancelAddAgency()"
      >
        Cancel
      </button>
      <button
        class="py-2.5 px-6 border-none rounded-lg bg-gray-900 text-white text-sm font-medium cursor-pointer transition-all hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
        (click)="confirmAddAgency()"
        [disabled]="!agencyFound"
      >
        <span class="text-base mr-1">+</span> Add Agency
      </button>
    </div>
  </div>
</div>

<!-- Bulk Upload Modal -->
<div
  class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-[1000] p-5"
  *ngIf="isBulkUploadModalOpen"
  (click)="onBulkUploadBackdropClick($event)"
>
  <div class="bg-white rounded-xl w-[500px] max-w-[90%] max-h-[calc(100vh-40px)] shadow-2xl relative flex flex-col" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="px-8 py-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 m-0">Bulk Upload Agencies</h3>
        <p class="text-sm text-gray-600 mt-1 m-0">{{ selectedStateAssociation?.name }}</p>
      </div>
      <button
        class="bg-transparent border-none text-2xl text-gray-400 cursor-pointer p-0 w-6 h-6 flex items-center justify-center leading-none hover:text-gray-500 transition-colors"
        (click)="cancelBulkUpload()"
        aria-label="Close"
        [disabled]="isProcessing"
      >
        ×
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-8 overflow-y-auto overflow-x-hidden flex-1 min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">

      <!-- Upload Instructions -->
      <div *ngIf="!showUploadSummary" class="mb-6">
        <p class="text-sm text-gray-700 mb-4">Upload a CSV or XLSX file containing an AgencyID column.</p>

        <button
          class="flex items-center gap-2 text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors bg-transparent border-none cursor-pointer p-0 mb-6"
          (click)="downloadTemplate()"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8 12L3 7h3V1h4v6h3z"/>
            <path d="M1 14h14v2H1z"/>
          </svg>
          Download sample template
        </button>

        <!-- UPDATED: Using drag-drop-zone component -->
        <app-drag-and-drop
          accept=".csv,.xlsx,.xls"
          (fileDropped)="handleBulkFile($event)"
        >
          <div class="w-12 h-12 mx-auto mb-3 rounded-md flex items-center justify-center text-3xl">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
            </svg>
          </div>
          <div class="text-sm text-gray-600 mb-1" *ngIf="!bulkUploadFileName">
            Drag and drop your file here, or
            <span class="text-blue-600 font-medium">browse files</span>
          </div>
          <div class="text-sm text-gray-900 font-medium mb-1" *ngIf="bulkUploadFileName">
            {{ bulkUploadFileName }}
          </div>
          <div class="text-xs text-gray-500">Supported formats: CSV, XLSX</div>
        </app-drag-and-drop>
      </div>

      <!-- Upload Summary -->
      <div *ngIf="showUploadSummary && uploadResult" class="space-y-4">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start gap-3">
            <div class="text-2xl">ℹ️</div>
            <div class="flex-1">
              <h4 class="text-sm font-semibold text-blue-900 mb-2 mt-0">Upload Complete</h4>
              <p class="text-sm text-blue-800 m-0">
                {{ uploadResult.successCount }} {{ uploadResult.successCount === 1 ? 'agency' : 'agencies' }}
                successfully associated with {{ selectedStateAssociation?.name }}.
              </p>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 rounded-lg p-5 space-y-3">
          <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Summary</div>

          <div class="flex justify-between items-center py-2 border-b border-gray-200">
            <span class="text-sm text-gray-700">Total agencies in file</span>
            <span class="text-sm font-semibold text-gray-900">{{ uploadResult.totalCount }}</span>
          </div>

          <div class="flex justify-between items-center py-2 border-b border-gray-200">
            <span class="text-sm text-gray-700 flex items-center gap-2">
              <span class="text-green-600">✓</span>
              Successfully added
            </span>
            <span class="text-sm font-semibold text-green-600">{{ uploadResult.successCount }}</span>
          </div>

          <div class="flex justify-between items-center py-2 border-b border-gray-200" *ngIf="uploadResult.duplicateCount > 0">
            <span class="text-sm text-gray-700 flex items-center gap-2">
              <span class="text-yellow-600">⚠</span>
              Skipped (duplicates)
            </span>
            <span class="text-sm font-semibold text-yellow-600">{{ uploadResult.duplicateCount }}</span>
          </div>

          <div class="flex justify-between items-center py-2" *ngIf="uploadResult.invalidCount > 0">
            <span class="text-sm text-gray-700 flex items-center gap-2">
              <span class="text-red-600">✗</span>
              Invalid/Not found
            </span>
            <span class="text-sm font-semibold text-red-600">{{ uploadResult.invalidCount }}</span>
          </div>
        </div>

        <!-- Invalid IDs List -->
        <div *ngIf="uploadResult.invalidIds.length > 0" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="text-sm font-medium text-red-900 mb-2">Invalid or non-existent Agency IDs:</div>
          <div class="max-h-32 overflow-y-auto space-y-1">
            <div *ngFor="let id of uploadResult.invalidIds" class="text-xs text-red-700 font-mono">
              {{ id }}
            </div>
          </div>
        </div>
      </div>

      <!-- Processing State -->
      <div *ngIf="isProcessing" class="flex flex-col items-center justify-center py-8">
        <div class="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
        <p class="text-sm text-gray-600">Processing agencies...</p>
      </div>
    </div>

    <!-- Modal Footer -->
    <div class="px-8 py-5 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white">
      <button
        class="py-2.5 px-5 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
        (click)="cancelBulkUpload()"
        [disabled]="isProcessing"
      >
        {{ showUploadSummary ? 'Close' : 'Cancel' }}
      </button>
      <button
        *ngIf="!showUploadSummary"
        class="py-2.5 px-6 border-none rounded-lg bg-gray-900 text-white text-sm font-medium cursor-pointer transition-all hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-60"
        (click)="processBulkUpload()"
        [disabled]="!bulkUploadFile || isProcessing"
      >
        <span *ngIf="!isProcessing">Upload File</span>
        <span *ngIf="isProcessing">Processing...</span>
      </button>
    </div>
  </div>
</div>

<!-- Delete Confirmation Modal -->
<div
  class="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center z-[1000] p-5"
  *ngIf="isDeleteModalOpen"
  (click)="onDeleteBackdropClick($event)"
>
  <div class="bg-white rounded-xl w-[360px] max-w-[90%] max-h-[calc(100vh-40px)] shadow-2xl relative flex flex-col" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="px-8 py-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
      <button
        class="bg-transparent border-none text-2xl text-gray-400 cursor-pointer p-0 w-6 h-6 flex items-center justify-center leading-none hover:text-gray-500 transition-colors"
        (click)="cancelDelete()"
        aria-label="Close"
      >
        ×
      </button>
    </div>

    <!-- Modal Body -->
    <div class="p-8 overflow-y-auto overflow-x-hidden flex-1 min-h-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-50 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
      <p class="text-sm text-gray-700 leading-relaxed m-0 text-left">
        Are you sure you want to remove <strong class="text-gray-900 font-semibold">{{ agencyToDelete?.name }}</strong> from the Association?
      </p>
    </div>

    <!-- Modal Footer -->
    <div class="px-8 py-5 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white">
      <button
        class="py-2.5 px-5 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm font-medium cursor-pointer transition-all hover:bg-gray-50 hover:border-gray-400"
        (click)="cancelDelete()"
      >
        Cancel
      </button>
      <button
        class="py-2.5 px-6 border-none rounded-lg bg-gray-900 text-white text-sm font-medium cursor-pointer transition-all hover:bg-gray-800"
        (click)="confirmDelete()"
      >
        Remove Agency
      </button>
    </div>
  </div>
</div>